import {publicAsset} from 'helpers/publicAsset'
import {Button, Image, Modal} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {denormalize} from 'normalizr'
import React, {useCallback, useContext, useMemo, useState,} from 'react'
import {useSelector} from 'react-redux'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {LayoutContext} from '../../../components/layouts/Default/LayoutContext'
import {getFullAddress, getResponseItem, LoadingWrapper, renderIf, renderIfElse} from '../../Shared'
import {showDeleteConfirmDialog} from '../factory/createConfirmDialog'
import createContextMenu from '../factory/createContextMenu'
import {bindQueryParam, mapPathParam} from '../functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from '../hooks/usePaginationHelper'
import {
    church_getById_Api,
    church_getChurchMassTimeById_Api,
    church_getMassTimesByChurch_Api,
    church_updateChurch_Api,
    churchMassTimeSchema,
    churchSchema,
} from "../../../apis";
import {useSelectEntities} from "../../../redux/entities/useSelectEntities";
import Async from "../../../modules/asyncCache/components/Async";
import {CreateChurchMassTimeForm, EditChurchMassTimeForm} from "./components/ChurchMassTimeForm";
import {EditChurchForm} from "./components/ChurchForm";
import useAsyncAction from "../../../modules/asyncCache/useAsyncAction";
import {notifyOnError} from "../factory/createErrorEvent";
import {NotificationActionTypes, successNotify} from "../factory/createNotification";
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import useAsync from "../../../modules/asyncCache/useAsync";
import {PermissionEntity} from "../Permissions/PermissionEntity";
import ChurchEntity from "./ChurchEntity";
import {getId} from "../../../apis/model/base";
import {useChurchMassTimes} from "./functions/useChurchMassTimes";
import {renderChurchMassTime} from "./functions/renderChurchMassTime";
import {GiPopeCrown} from "react-icons/gi";
import {addFavorite} from "../../../helpers/localStorage";
import TooltipApp from "components/TooltipApp";
import LoadingBlock from "../../Loading/LoadingBlock";
import {GoDotFill} from "react-icons/go";
import {PiCrossFill} from "react-icons/pi";
import {BsCalendar2Date, BsCalendar2Week, BsPinMapFill} from "react-icons/bs";

const ChurchContext = React.createContext({})

const CHURCH_EDIT = 'church_edit'

const GroupedMassTimeList = ({ids, renderItemFn}) => {
    const t = useTranslate()
    const items = useSelector(state =>
        denormalize(ids, [churchMassTimeSchema], state.entities) || []
    )

    const sundayItems = items.filter(m => m.sunday_mass_flag === 1)
    const saturdayItems = items.filter(m => !m.sunday_mass_flag && m.weekdays != null && m.weekdays[6] === '1')
    const weekdayItems = items.filter(m => !m.sunday_mass_flag && m.weekdays != null && m.weekdays.slice(1, 6).includes('1'))

    const renderGroup = (icon, labelKey, groupItems) => {
        if (!groupItems.length) return null
        return (
            <div key={labelKey} className="flex flex-col rounded-xl border border-color-50 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 flex items-center gap-2 border-b border-color-50">
                    {icon}
                    <span className="font-bold text-xs text-secondary-600 uppercase tracking-wider">
                        {t(labelKey)}
                    </span>
                </div>
                <div className="px-3 py-1 divide-y divide-gray-200">
                    {groupItems.map((item, index) => renderItemFn(item, index))}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {renderGroup(<PiCrossFill size={15} className="text-secondary-400"/>,'sunday masses group', sundayItems)}
            {renderGroup(<BsCalendar2Week size={13} className="text-secondary-400"/>, 'weekday masses group', weekdayItems)}
            {renderGroup(<BsCalendar2Date size={13} className="text-secondary-400"/>,'saturday masses group', saturdayItems)}
        </div>
    )
}


export const ChurchProvider = ({
                                   item,
                                   children,
                                   ...props
                               }) => {

    const apiInfo = church_getById_Api

    const id = getId(item) || item

    const cacheId = mapPathParam(apiInfo.path, {id})

    const {response, isLoading = false} = useAsync({
        apiInfo,
        query: bindQueryParam({id}),
    })

    const isSucceed = Number(_.get(response, 'status')) === 200

    const cache = getResponseItem(response)

    const error = getResponseItem(response, 'error')

    const errorMessage = _.get(response, 'errorMessages.error')

    const error_code = _.get(response, 'errorMessages.error_code')

    const status = {
        title: getResponseItem(response, 'status'),
        description: errorMessage,
    }


    const renderHeader = useCallback((action, isModal) => null, [])

    return (
        <LoadingWrapper isLoading={!item}>
            <ChurchEntity
                key={cacheId}
                item={id}>
                {(item) => {

                    if (item) {
                        addFavorite("churches", _.pick(item, ['_type', 'id', 'idname', 'name', 'photo']))
                    }
                    return (
                        <PermissionEntity item={item}>
                            <ChurchContext.Provider
                                value={{
                                    id,
                                    isLoading,
                                    item,
                                    error,
                                    cache,
                                    status,
                                    isSucceed,
                                    error_code,
                                    renderHeader,
                                    errorMessage,
                                    ...props,
                                }}>
                                {children}
                            </ChurchContext.Provider>
                        </PermissionEntity>
                    )
                }}
            </ChurchEntity>
        </LoadingWrapper>
    )
}

export const ChurchManager = ({
                                  church_id,
                                  Wrapper = 'div',
                                  filter_actions = {
                                      sortBy: 'sort-by',
                                      keyword: 'keyword',
                                  },
                              }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const church = useSelectEntities(church_id, churchSchema)

    const [refreshKey, setRefreshKey] = useState()
    const [action, setAction] = useState({type: undefined, value: undefined})
    const refresh_deps = [church_id, refreshKey]

    const onCancel = () => {
        setAction({})
    }
    const onSuccess = () => {
        onCancel()
        setRefreshKey(Date.now())
    }
    const {onCreate, onUpdate, onDelete} = useChurchMassTimes({
        church_id,
        notifyListeners: {
            onCreated: onSuccess,
            onUpdated: onSuccess,
            onDeleted: onSuccess,
        },
    })

    const {handleAsyncAction: onUpdateChurch} = useAsyncAction({
        onError: notifyOnError(t),
        apiInfo: church_updateChurch_Api,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const renderContextMenu = useCallback((item) => {
            return createContextMenu(
                [
                    {
                        icon: <BiSolidEdit className="text-blue-500"/>,
                        label: <span className="text-sm text-color-200">{t('edit')}</span>,
                        key: CRUD.UPDATE,
                    },
                    {
                        icon: <BiTrash className="text-primary-300"/>,
                        label: <span className="text-sm text-color-200">{t('delete')}</span>,
                        key: CRUD.DELETE,
                    },
                ],
                (key) => {
                    switch (key) {
                        case CRUD.UPDATE:
                            setAction({type: key, value: item})
                            break
                        case CRUD.DELETE:
                            const id = item?.id
                            if (id) {
                                showDeleteConfirmDialog({
                                    translate: t,
                                    title: 'delete this mass times',
                                    message: 'are you sure you want to delete this mass times',
                                    onOk: () => {
                                        onDelete(
                                            {},
                                            {
                                                id,
                                            }
                                        )
                                    },
                                })
                            }
                            break
                        default:
                            break
                    }
                }
            )
        }
        , [])

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        renderFilter: NullAsDefault,
        AdvancedFilter: NullAsDefault,
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParam({
                id: church_id,
            }),
            PaginationMeta: NullAsDefault,
            apiInfo: church_getMassTimesByChurch_Api,
            renderList: (list) => (
                <GroupedMassTimeList
                    ids={list}
                    renderItemFn={renderChurchMassTime({
                        translate: t,
                        renderContextMenu,
                        handleAction: (type, value) => {
                            setAction({type, value});
                        }
                    })}
                />
            ),
            renderWrap: (child) => (
                <div className="flex flex-col">
                    {child}
                </div>
            ),
        },
        renderHeader: ([searchBar, filterBar]) => (
            <React.Fragment>
                {filterBar}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        {searchBar}
                        {renderIf(
                            !!church?.edit,
                            <Button
                                type="primary"
                                className="button-rounded-lg no-border"
                                onClick={() => {
                                    setAction({type: CRUD.CREATE})
                                }}>
                                {t('add new')}
                            </Button>
                        )}
                    </div>
                    <div className="font-bold text-gray-600 uppercase tracking-wider">
                        {t('list of mass times of church')}
                    </div>
                </div>
            </React.Fragment>
        ),
    })

    const modal = useMemo(() => {
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {}
        let modal_params = {}
        switch (action.type) {
            case CRUD.CREATE:
                params = {
                    onSubmit: (values) => {
                        onCreate(values)
                    },
                }
                modal_params = {
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('create new mass times')}
                        </div>
                    ),

                }
                Content = CreateChurchMassTimeForm
                break
            case CRUD.UPDATE:
                params = {
                    onSubmit: (values) => {
                        onUpdate(values, {id: action.value?.id})
                    },
                }
                modal_params = {
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('edit')}
                        </div>
                    ),
                }
                Content = EditChurchMassTimeForm
                Wrapper = ({children}) => (
                    <Async
                        {...{
                            query: bindQueryParam({id: action.value?.id}),
                            apiInfo: church_getChurchMassTimeById_Api,
                        }}>
                        {({isLoading, response}) =>
                            _.isFunction(children)
                                ? children({
                                    isLoading,
                                    initialValues: getResponseItem(response),
                                })
                                : children
                        }
                    </Async>
                )
                break
            case CHURCH_EDIT:
                params = {
                    onSubmit: (values) => {
                        onUpdateChurch(values, {id: church_id})
                    },
                }
                modal_params = {
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('edit church info')}
                        </div>
                    ),
                    width: 720,
                }
                Content = EditChurchForm
                Wrapper = ({children}) => children({isLoading: false, initialValues: action.value})
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Wrapper>
                        {({isLoading, initialValues}) => {
                            if (!!isLoading) {
                                return <LoadingBlock/>
                            }
                            return (
                                <Content
                                    {...{
                                        onCancel,
                                        item: action.value,
                                    }}
                                    {...params}
                                    initialValues={initialValues}
                                />
                            )
                        }}
                    </Wrapper>
                </DelayRender>
            </Modal>
        )
    }, [action])

    return (
        <ChurchProvider item={church_id}>
            <ChurchContext.Consumer>
                {({item}) => {
                    const {
                        photo, cover_photo,
                        name,
                        contact_name,
                        contact_phone,
                        archdiocese,
                        diocese,
                        deanery,
                        parish,
                        is_cathedral,
                    } = item ?? {}

                    return (
                        <Wrapper className="flex flex-col">
                            {/* Photo — full edge with dual gradient + name overlay */}
                            <div className="relative custom-img-view rounded-t-xl" style={{lineHeight: 0}}>
                                <Image
                                    src={cover_photo || publicAsset('/images/find-a-church.webp')}
                                    fallback={publicAsset('/images/find-a-church.webp')}
                                    style={{height: isSm ? 160 : 200, width: '100%', objectFit: 'cover', display: 'block'}}
                                    wrapperStyle={{display: 'block', width: '100%', lineHeight: 0}}
                                    preview={false}
                                    className="rounded-t-xl" wrapperClassName="rounded-t-xl"
                                />
                                <div
                                    className="absolute inset-0 pointer-events-none rounded-t-lg"
                                    style={{background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 35%), linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 55%)'}}
                                />
                                {renderIf(
                                    !!is_cathedral,
                                    <TooltipApp title={t('cathedral')} placement="right"
                                                classNames={{root: "font-medium text-xs italic"}}>
                                        <GiPopeCrown
                                            size={30}
                                            style={{top: 10, left: 10}}
                                            className="absolute text-white opacity-75 hover:opacity-100 hover:text-primary-600 cursor-pointer"
                                        />
                                    </TooltipApp>
                                )}
                            </div>

                            {/* Church info */}
                            <div className="px-4 py-3 border-b border-color-50 flex flex-col gap-1 relative">
                                <h2 className="font-bold text-lg text-gray-800 tracking-wide leading-tight">
                                    {name}
                                </h2>
                                <div className="text-xs tracking-wider">
                                    <GoDotFill size={10} className="mr-1 text-slate-400"/>
                                    <span className="text-slate-400 whitespace-no-wrap">
                                        {`${t('belongs to')}: `}
                                    </span>
                                    <span className="font-medium text-slate-500">
                                        {[archdiocese, diocese, deanery, parish,]
                                            .filter(Boolean)
                                            .map(item => item.fullname)
                                            .join(', ')}
                                    </span>
                                </div>
                                <div className="space-x-1">
                                    <BsPinMapFill
                                        size={12}
                                        className="text-slate-400 flex-shrink-0"
                                        style={{marginTop: 2}}
                                    />
                                    <span className="text-xs text-color-300 tracking-wider">
                                        {getFullAddress(item)}
                                    </span>
                                </div>
                                {renderIf(
                                    _.some([contact_name, contact_phone]),
                                    <div className="text-xs tracking-wider">
                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                        <span className="text-color-400 whitespace-no-wrap italic">
                                            {`${t('contact')}: `}
                                        </span>
                                        {renderIfElse(
                                            contact_name && contact_phone,
                                            <span className="font-semibold text-color-100">
                                                {`${contact_name} - ${contact_phone}`}
                                            </span>,
                                            <span className="font-semibold text-color-100">
                                                {contact_name || contact_phone}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {renderIf(
                                    !!item?.edit,
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setAction({type: CHURCH_EDIT, value: item})}
                                            style={{padding: '2px 8px', cursor: 'pointer'}}
                                            className="flex items-center gap-1.5 font-semibold text-xs text-blue-600 hover:text-primary transition-all duration-300">
                                            <BiSolidEdit size={11}/>
                                            {t('edit church info')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Mass times list */}
                            <div className="px-4 py-3 flex flex-col gap-2">
                                {pager_widget}
                            </div>

                            {modal}
                        </Wrapper>
                    )
                }}
            </ChurchContext.Consumer>
        </ChurchProvider>
    )
}

export default ChurchContext